import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { DropZoneItemProps } from '../types/interfaces'

import { DROP_ZONE_STATUS } from '../types/enums'
import { formatFileSize } from '../utils/format-file-size'

interface UseDropZoneItemOptions {
  props: DropZoneItemProps
}

interface UseDropZoneItemReturn {
  formattedSize: ComputedRef<string>
  showProgress: ComputedRef<boolean>
  progressValue: ComputedRef<number>
  hasViewLink: ComputedRef<boolean>
  hasDownloadLink: ComputedRef<boolean>
  isError: ComputedRef<boolean>
  openView: () => void
  openDownload: () => void
}

export const useDropZoneItem = (
  options: UseDropZoneItemOptions,
): UseDropZoneItemReturn => {
  const { props } = options

  const formattedSize = computed<string>(() => formatFileSize(props.size))
  const showProgress = computed<boolean>(() => props.status === DROP_ZONE_STATUS.Uploading)
  const progressValue = computed<number>(() => props.progress ?? 0)
  const hasViewLink = computed<boolean>(() => !!props.viewLink)
  const hasDownloadLink = computed<boolean>(() => !!props.downloadLink)
  const isError = computed<boolean>(() => props.status === DROP_ZONE_STATUS.Error)

  const openView = (): void => {
    if (!props.viewLink) return
    window.open(props.viewLink, '_blank', 'noopener,noreferrer')
  }

  const openDownload = (): void => {
    if (!props.downloadLink) return
    const link = document.createElement('a')
    link.href = props.downloadLink
    link.download = props.name
    link.rel = 'noopener'
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  return {
    formattedSize,
    showProgress,
    progressValue,
    hasViewLink,
    hasDownloadLink,
    isError,
    openView,
    openDownload,
  }
}
